'use client';

import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import {
	Apple,
	ArrowRight,
	Box,
	ChevronDown,
	CirclePlus,
	Database,
	Package,
	Snowflake,
	Thermometer,
	Truck,
	Warehouse,
} from 'lucide-react';
import Image from 'next/image';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { type KeyboardEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { NavDropdownPanel, navDropdownItemVariants } from '@/components/NavDropdownPanel';
import { Button } from '@/components/ui/button';
import { ctfImage } from '@/lib/contentful-url';
import { newsHref } from '@/lib/routes';
import { LayoutQueryResponse } from '@/models/layout';
import { INews } from '@/models/news';

interface INavbar {
	layout: LayoutQueryResponse;
	blogs?: INews[];
}

type NavLink = {
	title: string;
	link: string;
	subtitle?: string;
	image?: string;
};

type NavSection = {
	title?: string;
	links: NavLink[];
};

type NavItem = {
	title: string;
	link: string;
	/** Expands inline on mobile. Everything else is a plain link there. */
	mobileDropdown?: boolean;
	dropdownStyle?: 'servicesMega' | 'insightsMega';
	viewAllLabel?: string;
	viewAllLink?: string;
	latestInsightsLabel?: string;
	sections?: NavSection[];
};

const MENU_CONFIG: {
	bannerText: string;
	bannerLink: string;
	searchLabel: string;
	searchLink: string;
	contactLabel: string;
	contactLink: string;
	trackLabel: string;
	trackLink: string;
	portalLabel: string;
	portalLink: string;
	loginLabel: string;
	loginLink: string;
	items: NavItem[];
} = {
	bannerText: 'Ready to open an account? Find your local branch here.',
	bannerLink: '/news',
	searchLabel: 'Search',
	searchLink: '/search',
	contactLabel: 'Contact Us',
	contactLink: '/contact',
	trackLabel: 'Track my Parcel',
	trackLink: '#',
	portalLabel: 'Customer Portal',
	portalLink: '#',
	loginLabel: 'Login',
	loginLink: 'https://worcs.lindnerlogistics.com/worcs/Login.htm',
	items: [
		{
			title: 'Solutions',
			link: '/warehousing-solutions',
			mobileDropdown: true,
			dropdownStyle: 'servicesMega',
			sections: [
				{
					title: 'Warehousing Solutions',
					links: [
						{
							title: 'General Storage',
							link: '/warehousing-solutions/general-storage',
						},
						{ title: 'Food Storage', link: '/warehousing-solutions/food-storage' },
						{ title: 'Cold Storage', link: '/warehousing-solutions/cold-storage' },
						{
							title: 'Freezer Storage',
							link: '/warehousing-solutions/freezer-storage',
						},
						{ title: 'WMS Technology', link: '/warehousing-solutions/wms-technology' },
					],
				},
				{
					title: 'Value-added Services',
					links: [
						{ title: 'Transportation', link: '/warehousing-solutions/transportation' },
						{
							title: 'Other Services',
							link: '/warehousing-solutions/value-added-services',
						},
					],
				},
			],
		},
		{ title: 'About', link: '/about', sections: [] },
		{ title: 'Facilities', link: '/facilities', sections: [] },
		{
			title: 'News',
			link: '/news',
			dropdownStyle: 'insightsMega',
			viewAllLabel: 'View all',
			viewAllLink: '/news',
			latestInsightsLabel: 'News',
			sections: [
				{
					links: [
						{
							title: 'Using FTZ-Enabled 3PL Warehousing to Offset Rising Tariffs and Import Costs',
							link: '/news',
							subtitle: 'March 5th 2026',
							image: 'https://images.ctfassets.net/tjz0w9gr80a0/5A3f9NkiRTnGtnsYDv3Gh9/057581f35f9f5c0ebb2c5507f4a99ba8/warehouse-news.jpg',
						},
						{
							title: 'How Midwest Cold Chain Hubs Help Manufacturers Cut Risk and Lead Times',
							link: '/news',
							subtitle: 'February 26th, 2026',
							image: 'https://images.ctfassets.net/tjz0w9gr80a0/4R8R4l9fWfI4kTttq36xqs/3e469f2dd3c8a332f52ed28b3987168f/cold-chain.jpg',
						},
						{
							title: 'Storing and Managing Temperature Sensitive Products',
							link: '/news',
							subtitle: 'February 19th, 2026',
							image: 'https://images.ctfassets.net/tjz0w9gr80a0/6A3nb24HfE48zQW1uGOVQ7/7f72603975ecf25eaab95bd9f6d35b59/temperature-sensitive.jpg',
						},
						{
							title: 'Talent and Technology: Human Side of Smart Warehousing in 2026',
							link: '/news',
							subtitle: 'February 12th, 2026',
							image: 'https://images.ctfassets.net/tjz0w9gr80a0/4EDQ9k4aX6hEt2vB4Q4nn2/6e1bf35934f68262fdb65f8fd5b4f717/facility-aerial.jpg',
						},
					],
				},
			],
		},
	],
};

function getServiceIcon(title: string) {
	const normalized = title.toLowerCase();
	if (normalized.includes('general storage')) return Warehouse;
	if (normalized.includes('food storage')) return Apple;
	if (normalized.includes('cold storage')) return Snowflake;
	if (normalized.includes('freezer storage')) return Thermometer;
	if (normalized.includes('wms')) return Database;
	if (normalized.includes('transport')) return Truck;
	if (normalized.includes('other services')) return CirclePlus;
	if (normalized.includes('storage')) return Box;
	return Package;
}

function openDropdownMenu(submenuKey: string) {
	const menu = document.getElementById(`nav-menu-${submenuKey}`);
	const firstLink = menu?.querySelector<HTMLElement>('a[href]');
	firstLink?.focus();
}

function handleNavDropdownKeyDown(
	e: KeyboardEvent<HTMLElement>,
	submenuKey: string,
	setOpenDropdown: (key: string | null) => void
) {
	if (e.key === ' ' || e.key === 'ArrowDown') {
		e.preventDefault();
		setOpenDropdown(submenuKey);
		requestAnimationFrame(() => openDropdownMenu(submenuKey));
	}
	if (e.key === 'Escape') {
		e.preventDefault();
		setOpenDropdown(null);
	}
}

export default function Navbar(props: INavbar) {
	void props.layout;
	const pathname = usePathname();
	const prefersReducedMotion = useReducedMotion();
	const [isMounted, setIsMounted] = useState(false);
	const [isMenuOpen, setIsMenuOpen] = useState(false);
	const [isNavHidden, setIsNavHidden] = useState(false);
	const [openMobileDropdown, setOpenMobileDropdown] = useState<string | null>(null);
	const [openDropdown, setOpenDropdown] = useState<string | null>(null);
	const dropdownRefs = useRef<Record<string, HTMLDivElement | null>>({});
	const dropdownCloseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	const cancelDropdownClose = useCallback(() => {
		if (dropdownCloseTimerRef.current) {
			clearTimeout(dropdownCloseTimerRef.current);
			dropdownCloseTimerRef.current = null;
		}
	}, []);

	const scheduleDropdownClose = useCallback(() => {
		cancelDropdownClose();
		dropdownCloseTimerRef.current = setTimeout(() => {
			setOpenDropdown(null);
			dropdownCloseTimerRef.current = null;
		}, 120);
	}, [cancelDropdownClose]);

	const closeDropdown = useCallback(() => {
		cancelDropdownClose();
		setOpenDropdown(null);
	}, [cancelDropdownClose]);

	const navItems = useMemo(
		() =>
			MENU_CONFIG.items.map((item, index) => ({
				...item,
				submenuKey:
					(item.sections?.length ?? 0) > 0 ? (`submenu-${index}` as string) : undefined,
			})),
		[]
	);
	const newsCards = useMemo(() => {
		return (props.blogs ?? [])
			.filter((blog) => Boolean(blog.coverImage?.url))
			.slice(0, 4)
			.map((blog) => ({
				title: blog.title,
				link: newsHref(blog),
				subtitle: new Date(blog.date).toLocaleDateString('en-US', {
					month: 'long',
					day: 'numeric',
					year: 'numeric',
				}),
				image: ctfImage(blog.coverImage?.url, { width: 480, quality: 70 }),
			}));
	}, [props.blogs]);

	useEffect(() => {
		let lastScrollY = window.scrollY;
		const handleScroll = () => {
			const current = window.scrollY;
			const diff = current - lastScrollY;
			lastScrollY = current;

			closeDropdown();

			if (Math.abs(diff) < 5) return;

			if (current < 100 || isMenuOpen) {
				setIsNavHidden(false);
			} else if (diff > 0) {
				setIsNavHidden(true);
			} else {
				setIsNavHidden(false);
			}
		};
		window.addEventListener('scroll', handleScroll, { passive: true });
		return () => window.removeEventListener('scroll', handleScroll);
	}, [isMenuOpen, closeDropdown]);

	useEffect(() => {
		void pathname;
		setIsMenuOpen(false);
		setOpenMobileDropdown(null);
		setOpenDropdown(null);
	}, [pathname]);

	useEffect(() => setIsMounted(true), []);

	/** Menu is a full-screen overlay on mobile, so the page behind it must not scroll. */
	useEffect(() => {
		if (!isMenuOpen) return;
		const previousOverflow = document.body.style.overflow;
		document.body.style.overflow = 'hidden';
		return () => {
			document.body.style.overflow = previousOverflow;
		};
	}, [isMenuOpen]);

	useEffect(() => {
		if (!isMenuOpen) return;
		const onKeyDown = (e: globalThis.KeyboardEvent) => {
			if (e.key === 'Escape') setIsMenuOpen(false);
		};
		const desktop = window.matchMedia('(min-width: 1024px)');
		const onBreakpointChange = (e: MediaQueryListEvent) => {
			if (e.matches) setIsMenuOpen(false);
		};
		document.addEventListener('keydown', onKeyDown);
		desktop.addEventListener('change', onBreakpointChange);
		return () => {
			document.removeEventListener('keydown', onKeyDown);
			desktop.removeEventListener('change', onBreakpointChange);
		};
	}, [isMenuOpen]);

	useEffect(() => {
		if (!openDropdown) return;
		const onKeyDown = (e: globalThis.KeyboardEvent) => {
			if (e.key === 'Escape') closeDropdown();
		};
		document.addEventListener('keydown', onKeyDown);
		return () => document.removeEventListener('keydown', onKeyDown);
	}, [openDropdown, closeDropdown]);

	useEffect(() => () => cancelDropdownClose(), [cancelDropdownClose]);

	return (
		<header
			className={`sticky top-0 z-50 bg-white transition-transform duration-300 ${
				isNavHidden ? '-translate-y-full' : 'translate-y-0'
			}`}
		>
			<nav className="container h-20 flex items-center justify-between gap-8">
				<Link href="/" prefetch={false} className="shrink-0">
					<Image
						priority
						src="/logo-dark.webp"
						sizes="164px"
						height={80}
						width={274}
						alt="Lindner logo"
						className="hidden lg:block h-12 w-auto"
					/>
					<Image
						priority
						src="/logo-dark.webp"
						sizes="110px"
						height={80}
						width={274}
						alt="Lindner logo"
						className="block lg:hidden h-8 w-auto"
					/>
				</Link>

				<div className="hidden lg:flex items-center justify-end h-full flex-1 gap-2">
					{navItems.map((nav) => {
						const isActive =
							pathname === nav.link || pathname.startsWith(`${nav.link}/`);
						if (nav.submenuKey) {
							const isDropdownOpen = openDropdown === nav.submenuKey;
							return (
								// biome-ignore lint/a11y/noStaticElementInteractions: closes keyboard-open menu when focus leaves item + panel
								<div
									key={nav.title}
									ref={(el) => {
										if (nav.submenuKey)
											dropdownRefs.current[nav.submenuKey] = el;
									}}
									className="relative h-full flex items-center"
									onMouseEnter={() => {
										cancelDropdownClose();
										if (nav.submenuKey) setOpenDropdown(nav.submenuKey);
									}}
									onMouseLeave={scheduleDropdownClose}
									onBlur={(e) => {
										const container = nav.submenuKey
											? dropdownRefs.current[nav.submenuKey]
											: null;
										if (!container?.contains(e.relatedTarget as Node)) {
											closeDropdown();
										}
									}}
								>
									<Link
										prefetch={false}
										href={nav.link}
										aria-haspopup="menu"
										aria-expanded={isDropdownOpen}
										aria-controls={`nav-menu-${nav.submenuKey}`}
										onFocus={() => {
											if (nav.submenuKey) setOpenDropdown(nav.submenuKey);
										}}
										onKeyDown={(e) => {
											if (!nav.submenuKey) return;
											handleNavDropdownKeyDown(
												e,
												nav.submenuKey,
												setOpenDropdown
											);
										}}
										className={`relative inline-flex h-full items-center px-3 font-semibold transition-colors duration-200 whitespace-nowrap after:absolute after:left-0 after:right-0 after:bottom-0 after:h-1 after:bg-[#1a325b] after:origin-center after:transition-transform after:duration-200 ${
											isActive
												? 'text-[#1a325b] !font-bold after:scale-x-100'
												: 'text-[#737373] hover:text-primary-700 after:scale-x-0 hover:after:scale-x-100'
										}`}
									>
										{nav.title}{' '}
										<ChevronDown
											className={`ml-1 h-3.5 w-3.5 transition-transform duration-300 ${
												isDropdownOpen ? 'rotate-180' : ''
											}`}
											aria-hidden
										/>
									</Link>
									<NavDropdownPanel
										isOpen={isDropdownOpen}
										menuId={`nav-menu-${nav.submenuKey}`}
										onClose={closeDropdown}
										onPanelMouseEnter={cancelDropdownClose}
										onPanelMouseLeave={scheduleDropdownClose}
									>
										{nav.dropdownStyle === 'servicesMega' ? (
											<div className="container py-6 space-y-6">
												{nav.sections?.map((section) => (
													<motion.div
														key={section.title ?? 'section'}
														variants={navDropdownItemVariants}
													>
														{section.title && (
															<p className="text-sm font-semibold text-primary-700 mb-4 border-b border-[#F5F5F5] pb-2">
																{section.title}
															</p>
														)}
														<div className="grid grid-cols-4 gap-2">
															{section.links.map((item) => (
																<motion.div
																	key={item.link}
																	variants={
																		navDropdownItemVariants
																	}
																>
																	<Link
																		prefetch={false}
																		href={item.link}
																		className="group/card flex items-center gap-2 rounded-sm hover:bg-slate-100 p-2 transition-colors"
																	>
																		<span className="inline-flex h-10 w-10 rounded shrink-0 items-center justify-center bg-slate-100 text-slate-500 group-hover/card:bg-[#1a325b] group-hover/card:text-white transition-all">
																			{(() => {
																				const Icon =
																					getServiceIcon(
																						item.title
																					);
																				return (
																					<Icon className="h-4 w-4" />
																				);
																			})()}
																		</span>
																		<span className="text-sm font-semibold text-slate-800">
																			{item.title}
																		</span>
																	</Link>
																</motion.div>
															))}
														</div>
													</motion.div>
												))}
											</div>
										) : (
											<div className="container py-6">
												<motion.div
													variants={navDropdownItemVariants}
													className="flex items-center justify-between mb-6 border-b border-[#F5F5F5] pb-2"
												>
													<p className="text-sm font-semibold text-primary-700">
														{nav.latestInsightsLabel ??
															'Latest Insights'}
													</p>
													{nav.viewAllLabel && nav.viewAllLink && (
														<Link
															prefetch={false}
															href={nav.viewAllLink}
															className="inline-flex items-center gap-1 text-sm text-primary-700"
														>
															{nav.viewAllLabel}{' '}
															<ArrowRight className="h-3.5 w-3.5" />
														</Link>
													)}
												</motion.div>
												<div className="grid grid-cols-4 gap-3">
													{newsCards.map((item) => (
														<motion.div
															key={item.link}
															variants={navDropdownItemVariants}
														>
															<Link
																prefetch={false}
																href={item.link}
																className="block hover:bg-slate-50 p-1 rounded transition-colors"
															>
																<img
																	src={item.image}
																	alt={item.title}
																	className="h-[138px] w-full object-cover rounded mb-2"
																	loading="lazy"
																/>
																<p className="text-base font-semibold text-[#1A335B] leading-tight mb-1">
																	{item.subtitle}
																</p>
																<p className="text-base text-[#0A0A0A] leading-snug line-clamp-2">
																	{item.title}
																</p>
																<p className="text-sm text-[#0161A2] mt-2 font-semibold">
																	Read article{' '}
																	<ArrowRight className="inline h-3.5 w-3.5" />
																</p>
															</Link>
														</motion.div>
													))}
												</div>
											</div>
										)}
									</NavDropdownPanel>
								</div>
							);
						}
						return (
							<Link
								key={nav.title}
								prefetch={false}
								href={nav.link}
								className={`relative inline-flex h-full items-center px-3 font-semibold transition-colors duration-200 whitespace-nowrap after:absolute after:left-0 after:right-0 after:bottom-0 after:h-1 after:bg-[#1a325b] after:origin-center after:transition-transform after:duration-200 ${
									isActive
										? 'text-[#1a325b] !font-bold after:scale-x-100'
										: 'text-[#737373] hover:text-primary-700 after:scale-x-0 hover:after:scale-x-100'
								}`}
							>
								{nav.title}
							</Link>
						);
					})}
				</div>

				<div className="hidden lg:flex items-center gap-2">
					<Link
						prefetch={false}
						href={MENU_CONFIG.loginLink}
						rel="nofollow"
						className="inline-flex h-10 items-center px-3  font-semibold text-[#0A0A0A] border border-[#CCCCCC] rounded hover:border-primary-700"
					>
						{MENU_CONFIG.loginLabel}
					</Link>
					<Link
						prefetch={false}
						href={MENU_CONFIG.contactLink}
						className="inline-flex h-10 items-center px-4  font-semibold text-white bg-[#1a325b] rounded hover:bg-primary-800"
					>
						{MENU_CONFIG.contactLabel}
					</Link>
				</div>

				<div className="lg:hidden flex items-center gap-2">
					<button
						type="button"
						className="text-base font-semibold px-4 h-[42px] rounded-[4px]  border-[#cccccc] border"
						aria-controls="mobile-menu"
						aria-expanded={isMenuOpen}
						onClick={() => {
							setIsMenuOpen((prev) => !prev);
							setOpenMobileDropdown(null);
						}}
					>
						<span className="sr-only">Toggle menu</span>
						{isMenuOpen ? 'Close' : 'Menu'}
					</button>
					<Link
						prefetch={false}
						href={MENU_CONFIG.contactLink}
						className="hidden sm:inline-flex h-10 items-center px-4 font-semibold text-white bg-[#1a325b] rounded hover:bg-primary-800"
					>
						{MENU_CONFIG.contactLabel}
					</Link>
				</div>
			</nav>
			<div className="w-full h-[1px] bg-[#CCCCCC]" />

			{/* Portaled so the header transform does not trap fixed positioning, and so the
			    open menu overlays the page instead of pushing it down. */}
			{isMounted &&
				createPortal(
					<AnimatePresence>
						{isMenuOpen && (
							<>
								<motion.button
									key="mobile-menu-backdrop"
									type="button"
									className="lg:hidden fixed inset-x-0 bottom-0 top-[81px] z-40 bg-[#0A0A0A]/45"
									initial={prefersReducedMotion ? false : { opacity: 0 }}
									animate={{ opacity: 1 }}
									exit={{ opacity: 0 }}
									transition={{ duration: 0.2, ease: 'easeOut' }}
									onClick={() => setIsMenuOpen(false)}
									aria-label="Close menu backdrop"
								/>
								<motion.div
									key="mobile-menu"
									id="mobile-menu"
									className="lg:hidden fixed inset-x-0 top-[81px] z-[49] flex max-h-[calc(100dvh-81px)] flex-col bg-white"
									initial={prefersReducedMotion ? false : { opacity: 0, y: -12 }}
									animate={{ opacity: 1, y: 0 }}
									exit={
										prefersReducedMotion
											? { opacity: 0 }
											: { opacity: 0, y: -12 }
									}
									transition={{ duration: 0.25, ease: 'easeOut' }}
								>
									<div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 pt-4 pb-6 flex flex-col">
										{navItems.map((nav) => {
											const isActive =
												pathname === nav.link ||
												pathname.startsWith(`${nav.link}/`);
											const label = (
												<span
													className={
														isActive
															? 'border-b-[3px] border-primary-500 pb-0.5'
															: undefined
													}
												>
													{nav.title}
												</span>
											);

											if (!nav.mobileDropdown) {
												return (
													<Link
														key={nav.title}
														prefetch={false}
														href={nav.link}
														className="py-4 text-2xl font-bold text-[#0A0A0A]"
													>
														{label}
													</Link>
												);
											}

											const isOpen = openMobileDropdown === nav.title;
											const panelId = `mobile-submenu-${nav.title.toLowerCase()}`;
											return (
												<div key={nav.title}>
													<button
														type="button"
														aria-expanded={isOpen}
														aria-controls={panelId}
														onClick={() =>
															setOpenMobileDropdown(
																isOpen ? null : nav.title
															)
														}
														className="cursor-pointer flex w-full items-center justify-between py-4 text-2xl font-bold text-[#0A0A0A]"
													>
														{label}
														<ChevronDown
															className={`h-5 w-5 shrink-0 transition-transform duration-300 ${
																isOpen ? 'rotate-180' : ''
															}`}
															aria-hidden
														/>
													</button>
													<AnimatePresence initial={false}>
														{isOpen && (
															<motion.div
																id={panelId}
																initial={{ height: 0, opacity: 0 }}
																animate={{
																	height: 'auto',
																	opacity: 1,
																}}
																exit={{ height: 0, opacity: 0 }}
																transition={{
																	duration: 0.25,
																	ease: 'easeInOut',
																}}
																className="overflow-hidden"
															>
																<div className="flex flex-col gap-6 pb-4">
																	{nav.sections?.map(
																		(section) => (
																			<div
																				key={
																					section.title ??
																					'section'
																				}
																				className="flex flex-col gap-3"
																			>
																				{section.title && (
																					<p className="text-base font-semibold text-[#0A0A0A]">
																						{
																							section.title
																						}
																					</p>
																				)}
																				<div className="flex flex-col gap-2">
																					{section.links.map(
																						(link) => (
																							<Link
																								key={
																									link.link
																								}
																								prefetch={
																									false
																								}
																								href={
																									link.link
																								}
																								className="flex items-center gap-4 rounded-lg py-1 text-base text-[#0A0A0A] hover:bg-[#F5F5F5]"
																							>
																								<span className="inline-flex h-[32px] w-[32px] shrink-0 items-center justify-center rounded-[4px] bg-primary-500 text-white">
																									{(() => {
																										const Icon =
																											getServiceIcon(
																												link.title
																											);
																										return (
																											<Icon className="h-5 w-5" />
																										);
																									})()}
																								</span>
																								<span>
																									{
																										link.title
																									}
																								</span>
																							</Link>
																						)
																					)}
																				</div>
																			</div>
																		)
																	)}
																</div>
															</motion.div>
														)}
													</AnimatePresence>
												</div>
											);
										})}
									</div>

									<div className="shrink-0 border-t border-[#E5E5E5] px-4 py-4 space-y-2">
										<Button
											asChild
											variant="outline"
											className="w-full !h-10 !rounded !px-3 font-semibold !border-[#CCCCCC] !bg-white !text-[#0A0A0A] hover:!bg-white hover:!border-primary-700"
										>
											<Link
												prefetch={false}
												href={MENU_CONFIG.loginLink}
												rel="nofollow"
											>
												{MENU_CONFIG.loginLabel}
											</Link>
										</Button>
										<Button
											asChild
											variant="default"
											className="flex sm:hidden w-full !h-10 !rounded !px-4  font-semibold !bg-[#1a325b] !text-white hover:!bg-primary-800"
										>
											<Link prefetch={false} href={MENU_CONFIG.contactLink}>
												{MENU_CONFIG.contactLabel}
											</Link>
										</Button>
									</div>
								</motion.div>
							</>
						)}
					</AnimatePresence>,
					document.body
				)}
		</header>
	);
}
